import React, { CSSProperties, useMemo } from "react";

/**
 * Reusable Skeleton primitives + LoadingWrapper
 * - TailwindCSS
 * - TypeScript
 * - Accessible (role="status", aria-busy)
 *
 * Usage examples at bottom of file.
 */

// ---------- utils ----------
const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const toStyleDim = (v?: number | string): string | undefined => {
  if (v === undefined) return undefined;
  if (typeof v === "number") return `${v}px`;
  return v;
};

// Simple seeded random for stable widths across renders (avoids hydration flicker)
const seededRand = (seed: number) => {
  let s = seed % 2147483647;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
};

// ---------- core primitive ----------
export type SkeletonBaseProps = {
  /** width (px or %, number -> px) */
  w?: number | string;
  /** height (px or %, number -> px) */
  h?: number | string;
  /** set to false to disable pulse animation */
  animated?: boolean;
  /** additional classes */
  className?: string;
  /** additional inline styles */
  style?: CSSProperties;
  /** tailwind rounding (e.g., 'rounded', 'rounded-md', 'rounded-full') */
  roundedClass?: string;
  /** for a11y labelling when used standalone */
  ariaLabel?: string;
};

export const SkeletonBase: React.FC<SkeletonBaseProps> = ({
  w = "100%",
  h = 16,
  animated = true,
  className,
  style,
  roundedClass = "rounded",
  ariaLabel = "Loading",
}) => {
  const width = toStyleDim(w);
  const height = toStyleDim(h);
  return (
    <div
      role="status"
      aria-busy={true}
      aria-label={ariaLabel}
      className={cx(
        animated && "animate-pulse",
        "bg-gray-300",
        roundedClass,
        className
      )}
      style={{ width, height, ...style }}
    />
  );
};

// ---------- convenient variants ----------
export type SkeletonLineProps = Omit<SkeletonBaseProps, "h"> & { h?: number | string };
export const SkeletonLine: React.FC<SkeletonLineProps> = ({ h = 16, ...rest }) => (
  <SkeletonBase h={h} {...rest} />
);

export type SkeletonCircleProps = Omit<SkeletonBaseProps, "roundedClass"> & {
  size?: number | string;
};
export const SkeletonCircle: React.FC<SkeletonCircleProps> = ({ size = 40, className, ...rest }) => (
  <SkeletonBase
    w={size}
    h={size}
    roundedClass="rounded-full"
    className={className}
    {...rest}
  />
);

export type SkeletonAvatarProps = SkeletonCircleProps;
export const SkeletonAvatar = SkeletonCircle;

export type SkeletonBlockProps = SkeletonBaseProps;
export const SkeletonBlock: React.FC<SkeletonBlockProps> = (props) => <SkeletonBase {...props} />;

export type SkeletonTextProps = {
  /** number of lines */
  lines?: number;
  /** widths for each line. If omitted, uses seeded random between range */
  widths?: Array<number | string> | "random";
  /** min/max width (percent) for random widths */
  randomRange?: [number, number];
  /** height of each line */
  lineHeight?: number | string;
  /** gap between lines; default: space-y-2 */
  gapClassName?: string;
  /** outer wrapper classes */
  className?: string;
  /** line classes (e.g., to change rounding) */
  lineClassName?: string;
  /** seed so widths remain stable across re-renders/SSR */
  seed?: number;
  /** disable animation */
  animated?: boolean;
};

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  widths,
  randomRange = [60, 100],
  lineHeight = 16,
  gapClassName = "space-y-2",
  className,
  lineClassName,
  seed = 42,
  animated = true,
}) => {
  const widthsComputed = useMemo(() => {
    if (Array.isArray(widths)) return widths;
    const rand = seededRand(seed);
    const [min, max] = randomRange;
    return Array.from({ length: lines }, (_, i) => {
      // Make last line a bit shorter for visual rhythm
      const base = i === lines - 1 ? Math.max(min, max - 20) : min;
      const pct = base + (max - base) * rand();
      return `${Math.round(pct)}%`;
    });
  }, [widths, randomRange, lines, seed]);

  return (
    <div className={cx(gapClassName, className)} aria-hidden>
      {widthsComputed.map((w, i) => (
        <SkeletonLine
          key={i}
          w={w}
          h={lineHeight}
          animated={animated}
          className={lineClassName}
        />
      ))}
    </div>
  );
};

export type SkeletonTableRowsProps = {
  rows?: number;
  cols?: number;
  /** provide per-column widths or a function; else uses random % */
  colWidths?: Array<number | string> | ((colIndex: number) => number | string);
  rowHeight?: number | string;
  rowGapClassName?: string;
  seed?: number;
  animated?: boolean;
  className?: string;
};

export const SkeletonTableRows: React.FC<SkeletonTableRowsProps> = ({
  rows = 3,
  cols = 3,
  colWidths,
  rowHeight = 16,
  rowGapClassName = "space-y-2",
  seed = 101,
  animated = true,
  className,
}) => {
  const rand = useMemo(() => seededRand(seed), [seed]);
  const getWidth = (c: number) => {
    if (Array.isArray(colWidths)) return colWidths[c % colWidths.length];
    if (typeof colWidths === "function") return colWidths(c);
    const pct = 60 + Math.round(rand() * 40); // 60% - 100%
    return `${pct}%`;
  };

  return (
    <div className={cx(rowGapClassName, className)} aria-hidden>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <SkeletonLine key={c} w={getWidth(c)} h={rowHeight} animated={animated} />)
          )}
        </div>
      ))}
    </div>
  );
};
